import {
  differentiate,
  evaluateAt,
  formatQuantity,
  graphWindowOf,
  NotableKind,
  notablePointsOf,
  parseExpression,
  sampleFunction,
  windowBox,
} from '../expression';
import {
  conics,
  intersect as geomIntersect,
  lines,
  polygon as polygonOps,
  polygonBoolean as booleanOps,
  type Segment,
} from '../geometry';
import { angle, EPSILON, mat, type Vec2, vec } from '../math';
import {
  type Vec3,
  add3,
  angleBetween,
  chordInRing,
  length3,
  normalize3,
  planeBasis,
  projectionFrame,
  rotateAbout,
  scale3,
  subtract3 as sub3,
  buildNamedSolid,
  hullOf,
  NO_ROTATION,
  projectMesh,
  ProjectionKind,
  type SolidParameters,
  axialSection,
  axisInPlane,
  chordOf,
  ellipseFromDiameters,
  footOnPlane,
  meetPlanes,
  onPageOf,
  perpendicularFoot,
  type Plane,
  planeParallel,
  planeThrough,
  projectOnto,
  projectSolid,
  rimPoint,
  ringOf,
  type RoundBody,
  roundSection,
  sectionOf,
  turnPlane,
  type SolidFrame,
  vec3,
} from '../solid';

import { closestPoint, intersectCurves, parameterAt, pointAt } from './curve';
import {
  asCircle,
  asCurve,
  asLine,
  asVertices,
  asPoint,
  type Curve,
  hostCurve,
  type Evaluated,
  finitePoint,
  finiteSpacePoint,
  ANGLE_MARK_LABEL_GAP,
  angleMarkOuterRadius,
  angleMarkRadius,
  angleMarkValueDistance,
  SEGMENT_CAPTION_GAP,
} from './evaluated';
import { isFiniteValue } from './finite';
import { measure } from './measure';
import { type EntitySource } from './source';
import { layOutVariationTable, variationTableFor } from './variationTable';

/**
 * How far from 90° an angle may be and still be drawn as a right angle.
 *
 * In degrees, not as a dot-product epsilon: a dot product scales with the arms' lengths, so
 * a tolerance expressed that way would mark a short angle and refuse the same angle drawn
 * larger. A tenth of a degree is finer than any drawing can express and far above the noise
 * of the arithmetic.
 */
const RIGHT_ANGLE_TOLERANCE_DEGREES = 0.1;

/**
 * Whether the angle is right *in space*, or `null` when the drawing is all there is to go on.
 *
 * An oblique projection keeps only the angles lying in a plane parallel to the picture plane —
 * for a cabinet drawing, the front face and nothing else. `SA ⊥ AD` at the foot of a pyramid is
 * a genuine right angle that reaches the page as 60°, and arms at some other angle in space can
 * project to a clean 90° and earn a square the solid never claimed. Whenever all three arms
 * remember where they are, that is the question worth asking.
 */
function squareOf(
  a: Evaluated | null | undefined,
  vertex: Evaluated | null | undefined,
  c: Evaluated | null | undefined
): boolean | null {
  const degrees = spaceAngleOf(a, vertex, c);
  return degrees === null ? null : Math.abs(degrees - 90) < RIGHT_ANGLE_TOLERANCE_DEGREES;
}

/**
 * The angle at the vertex **in space**, in degrees, when all three points know where they are.
 *
 * ## Why this is the number, and the drawn one is not
 *
 * A projection does not preserve angles. An angle marked on a solid is an angle *of the solid* —
 * it lies in the plane those three points span — and the wedge on the page is its shadow, opened
 * or closed by however the figure happens to be turned. Reading the shadow gives a real number
 * about nothing: a right angle in a cube measures 60° or 120° on the page depending on the face
 * it is on, and a caption saying so is worse than no caption.
 *
 * `null` for a flat figure, where the points carry no position in space — and there the page
 * *is* the plane, so the drawn angle is the angle and needs no correction.
 */
function spaceAngleOf(
  a: Evaluated | null | undefined,
  vertex: Evaluated | null | undefined,
  c: Evaluated | null | undefined
): number | null {
  const arms = [a, vertex, c].map((value) => (value?.kind === 'point' ? value.in3d : undefined));
  const [from, at, to] = arms;
  if (from === undefined || at === undefined || to === undefined) return null;

  const u = vec3(from.x - at.x, from.y - at.y, from.z - at.z);
  const w = vec3(to.x - at.x, to.y - at.y, to.z - at.z);
  const lengths = Math.hypot(u.x, u.y, u.z) * Math.hypot(w.x, w.y, w.z);
  if (lengths === 0) return null;

  const cosine = (u.x * w.x + u.y * w.y + u.z * w.z) / lengths;
  return (Math.acos(Math.min(1, Math.max(-1, cosine))) * 180) / Math.PI;
}

/**
 * A point a fraction of the way along, carrying its position in space when both ends have one.
 *
 * Projection is affine and so is dividing a segment, so the preimage of the divided point on the
 * page is exactly the same division of the preimages — no approximation. That is what lets
 * `M = midpoint A B` on a cube still know which side of the block it is on, and it is why a
 * ratio is one of the very few metric-sounding statements a projected figure can carry.
 */
function dividing(
  first: Evaluated | null | undefined,
  second: Evaluated | null | undefined,
  t: number
): Evaluated | null {
  const a = asPoint(first);
  const b = asPoint(second);
  if (a === null || b === null) return null;

  const at = vec.lerp(a, b, t);
  const from = first?.kind === 'point' ? first.in3d : undefined;
  const to = second?.kind === 'point' ? second.in3d : undefined;
  if (from === undefined || to === undefined) return finitePoint(at);

  return finiteSpacePoint(
    at,
    vec3(from.x + (to.x - from.x) * t, from.y + (to.y - from.y) * t, from.z + (to.z - from.z) * t)
  );
}

/**
 * Tier-1 evaluation: a construction plus the values of its parents, in, one value out.
 *
 * Pure and exact. There is no iteration, no tolerance-driven search and no state — every
 * operation here has a closed form, which is precisely why ADR-0003 makes this the
 * primary path and leaves the numeric solver for the handful of relational constraints
 * that genuinely need it.
 *
 * `parents` arrives in the same order as `parentsOf` produced it. Any parent that is
 * itself undefined arrives as `null`, and almost every operation then returns `null` in
 * turn — undefinedness propagates down the DAG exactly as it should, so a construction
 * built on a vanished intersection vanishes with it and returns when it returns.
 */
export function evaluate(
  source: EntitySource,
  parents: readonly (Evaluated | null)[]
): Evaluated | null {
  const value = evaluateSource(source, parents);

  /*
   * The last gate before a value reaches the scene.
   *
   * `NaN` means the same thing `null` does — there is no answer — but says it in a form that
   * spreads: it compares false against every guard downstream, propagates into every dependent
   * construction, and surfaces as broken geometry far from its cause. Collapsing it to `null`
   * here restores the kernel's actual contract, once, for every construction there will ever
   * be. See `finite.ts` for what a sweep of all thirty-eight found.
   */
  return value !== null && isFiniteValue(value) ? value : null;
}

function evaluateSource(
  source: EntitySource,
  parents: readonly (Evaluated | null)[]
): Evaluated | null {
  switch (source.op) {
    /* ------------------------------------------------------------------ free */

    case 'free-point':
      return finitePoint(source.position);

    case 'free-polyline':
      return { kind: 'polyline', points: source.points, closed: source.closed };

    /*
     * The one construction whose parents are a *formula* rather than other entities.
     *
     * Sampling happens here, in the evaluator, so a graph behaves like every other value: it
     * is recomputed from its source, it participates in the scene, and an unparseable formula
     * evaluates to nothing exactly as a degenerate circle does. A malformed expression is a
     * degenerate construction, not an error.
     */
    case 'axes':
      return buildAxes({
        scale: source.scale,
        // Absent means equal units, which is what every file written before this and every
        // geometry figure means by leaving it out.
        scaleY: source.scaleY ?? source.scale,
        extent: source.extent,
        extentY: source.extentY ?? source.extent,
        step: source.step,
        stepY: source.stepY ?? source.step,
        numbered: source.numbered,
        center: source.center ?? vec.vec2(0, 0),
      });

    /*
     * The table reads its formula out of the graph it names, so the two cannot disagree.
     *
     * It evaluates to nothing when there is no table to be had — a periodic function, a
     * staircase with no derivative — which is the same answer a circumcircle gives for three
     * collinear points, and it behaves the same way: the object disappears from the drawing
     * and comes back if the formula is edited into something tabulable. The panel says why
     * before the user commits, so this is the safety net rather than the explanation.
     */
    case 'variation-table':
      return buildVariationGeometry(parents[0] ?? null, source.at);

    /*
     * The notable points of a curve, as one derived object.
     *
     * Marks placed as plain points would be correct when made and silently wrong from the next
     * edit on — retype `x^2` as `x^3` and the dots stay at the parabola's turning point, still
     * labelled with its coordinates, on a curve that has none there. Reading them off the parent
     * value means they simply follow.
     *
     * The expression comes from the *evaluated* graph rather than from a source somewhere else,
     * for the same reason the variation table takes it that way: two objects reading one formula
     * from two places is how a figure ends up describing a function it is not drawn beside.
     */
    case 'graph-marks': {
      const curve = parents[0];
      if (curve?.kind !== 'function-graph') return null;

      let parsed;
      try {
        parsed = parseExpression(curve.expression);
      } catch {
        return null;
      }

      const kinds = source.kinds.filter((kind): kind is NotableKind =>
        (Object.values(NotableKind) as string[]).includes(kind)
      );
      // Nothing ticked draws nothing. The object stays, so unticking the last box does not
      // delete it out from under whoever is unticking.
      if (kinds.length === 0) return { kind: 'graph-marks', points: [], rules: [], marks: [] };

      const found = notablePointsOf(parsed, curve.from, curve.to, kinds);
      const s = curve.scale;

      const points = found.map((point) => vec.vec2(point.x * s, point.y * s));
      const rules: Segment[] = [];
      const marks: { at: Vec2; text: string }[] = [];

      for (const point of found) {
        const at = vec.vec2(point.x * s, point.y * s);
        if (!source.guides) {
          marks.push({ at: vec.add(at, markLabelOffset(point.kind)), text: coordinateText(point) });
          continue;
        }

        /*
         * The number always; the line only when there is a gap to cross.
         *
         * A guide is worth nothing without the value it lands on — the tick spacing is chosen
         * for legibility, so a point at x = 3 in a frame stepping by 2 has no tick there and the
         * dashed line arrives at a blank stretch of axis. But the two are separate decisions,
         * and conflating them was wrong in the case that matters most: a **root** is already on
         * `Ox`, so it needs no line at all — and the first cut read that as "nothing to do" and
         * dropped the number too, then drew a pointless horizontal line to the origin labelled
         * `0`. A textbook prints the root's value under the axis and draws no line. This does.
         *
         * A coordinate that is zero is skipped outright: it would be written at the origin, on
         * top of the origin's own `0`.
         */
        if (Math.abs(point.x) > EPSILON) {
          const foot = vec.vec2(at.x, 0);
          if (Math.abs(point.y) > EPSILON) rules.push({ a: at, b: foot });
          marks.push({ at: vec.add(foot, AXIS_NUMBER_BELOW), text: formatQuantity(point.x) });
        }
        if (Math.abs(point.y) > EPSILON) {
          const foot = vec.vec2(0, at.y);
          if (Math.abs(point.x) > EPSILON) rules.push({ a: at, b: foot });
          marks.push({ at: vec.add(foot, AXIS_NUMBER_LEFT), text: formatQuantity(point.y) });
        }
      }

      return { kind: 'graph-marks', points, rules, marks };
    }

    /*
     * The convex body on a set of points that know where they are in space.
     *
     * Every ingredient is already here: the corners carry `in3d` because every construction in
     * this file that produces a point in space passes it on, `hullOf` turns those into a mesh,
     * and `projectMesh` draws any mesh with its hidden edges cut exactly. So this case is a
     * three-line join rather than a new drawing path — which is the argument for having built
     * those three separately.
     *
     * `null` when a corner has no position in space, or when the points do not describe a body:
     * four in a plane are a quadrilateral, and drawing something anyway would put a solid on the
     * page that the figure never contained.
     */
    /*
     * The piece a plane leaves of a body, named by a corner it keeps.
     *
     * Nothing is clipped. The piece a plane leaves of a **convex** body is convex, and its corners
     * are exactly the original corners on the kept side together with the corners of the section —
     * so collecting those and taking their hull is not an approximation of the answer, it is the
     * answer. The section's corners come from the plane, which has already worked out where the
     * cut meets each edge; asking for them again here would be a second implementation of the one
     * piece of geometry this depends on.
     *
     * A corner exactly *on* the plane belongs to both pieces and is kept either way, which is what
     * makes a cut through a vertex come out with that vertex rather than a hole where it was.
     */
    case 'solid-cut': {
      const body = parents[0];
      const section = parents[1];
      const keeps = parents[2];
      if (body?.kind !== 'solid' || section?.kind !== 'polygon') return null;
      if (keeps?.kind !== 'point' || keeps.in3d === undefined) return null;

      const cut = section.in3d;
      const corners = body.in3d;
      if (cut === undefined || cut.length < 3 || corners === undefined) return null;

      const plane = planeFromSection(section);
      if (plane === null) return null;

      const side = (point: Vec3): number =>
        (point.x - plane.origin.x) * plane.normal.x +
        (point.y - plane.origin.y) * plane.normal.y +
        (point.z - plane.origin.z) * plane.normal.z;

      /*
       * Which side is wanted, read off the corner the writer named. The plane's normal has an
       * arbitrary sign — it depends on the order its three points happened to be given — so the
       * only honest way to say "this half" is to point at something in it.
       */
      const wanted = Math.sign(side(keeps.in3d));
      if (wanted === 0) return null;

      const spread = Math.max(...corners.map((corner) => Math.abs(side(corner))), 1);
      const kept = corners.filter((corner) => side(corner) * wanted > -spread * 1e-9);

      const mesh = hullOf([...kept, ...cut]);
      if (mesh === null) return null;

      const wire = projectMesh(mesh, {
        parameters: { kind: 'prism' } as unknown as SolidParameters,
        projection: ProjectionKind.Cabinet,
        rotation: NO_ROTATION,
        origin: vec.vec2(0, 0),
      });

      return {
        kind: 'solid',
        origin: vec.vec2(0, 0),
        visible: wire.visible,
        hidden: wire.hidden,
        ...(wire.corners === undefined
          ? {}
          : {
              points: wire.corners.points,
              in3d: wire.corners.in3d,
              faces: wire.corners.faces,
              frame: wire.corners.frame,
            }),
      };
    }

    case 'polyhedron': {
      const corners = parents.map((parent) => (parent?.kind === 'point' ? parent.in3d : undefined));
      if (corners.some((corner) => corner === undefined)) return null;

      const mesh = hullOf(corners.filter((corner): corner is Vec3 => corner !== undefined));
      if (mesh === null) return null;

      const view = {
        parameters: { kind: 'prism' } as unknown as SolidParameters,
        // The one view every named solid is drawn in, so a hull sits beside them rather than in
        // a projection of its own.
        projection: ProjectionKind.Cabinet,
        rotation: NO_ROTATION,
        origin: vec.vec2(0, 0),
      };
      const wire = projectMesh(mesh, view);

      return {
        kind: 'solid',
        origin: vec.vec2(0, 0),
        visible: wire.visible,
        hidden: wire.hidden,
        // The corners and faces, so a plane can cut this body exactly as it cuts a named one.
        ...(wire.corners === undefined
          ? {}
          : {
              points: wire.corners.points,
              in3d: wire.corners.in3d,
              faces: wire.corners.faces,
              frame: wire.corners.frame,
            }),
      };
    }

    case 'function-graph': {
      let parsed;
      try {
        parsed = parseExpression(source.expression);
      } catch {
        return null;
      }

      const sampled = sampleFunction(parsed, { from: source.from, to: source.to });

      /*
       * Clipped to the frame the axes are drawn to, which is a correction rather than a choice.
       *
       * The limit was `(to - from) / 2` — half the x-span — from when a graph's window really
       * was as tall as it is wide. `graphWindow` replaced that rule for the *axes* and this line
       * was left behind, so the two disagreed and the disagreement was silent: `y = x²−4x+3`
       * over [−1, 5] was cut at |y| = 3 inside axes drawn 8 units tall, and what a reader saw
       * was the curve over [0, 4] — both ends of the stated interval simply absent, with a frame
       * around it insisting they were in range.
       *
       * Measured, not guessed: the plotted x-span was 0.0017 to 4.0 out of the [−1, 5] asked
       * for, and `y = x²` over [−3, 3] came out spanning ±1.73.
       *
       * Some clipping is still right — a curve is drawn on a page, and near a vertical asymptote
       * it runs off the top. It is the *same* number as the axes now, taken from the samples
       * already in hand so the two cannot drift apart again.
       */
      const window = graphWindowOf(sampled, source.from, source.to);
      const box = windowBox(window);
      /*
       * The y unit, and **only** what the source states.
       *
       * The first version fell back to the window's own suggestion when the field was absent, on
       * the reasoning that the axes are fitted from the same window so the two would agree. They
       * do not, and cannot: an `axes` that says nothing about its y unit draws at `scale` both
       * ways, so a curve that quietly compressed itself was drawn against a scale nobody had
       * written. Measured on `axes 10` with `graph "x^3-3x" -3 3`, which is an ordinary line of
       * the script language: at `x = 1` the curve should sit at −80 world units and sat at −42.9,
       * so a reader taking the value off the axis read −1.07 for −2.
       *
       * It would also have re-drawn every graph already saved, since none of them carries the
       * field — a figure changing shape on being reopened is the worst of the two.
       *
       * Compression belongs to whoever fits the *pair*. `addFunctionGraph` and `fitAxesTo` both
       * write the number into the axes and the curve together, which is what makes them agree —
       * by being told the same thing, not by both working it out.
       */
      const scaleY = source.scaleY ?? source.scale;
      const segments = clipToWindow(sampled, box.minY, box.maxY, source.scale, scaleY);
      if (segments.length === 0) return null;

      return {
        kind: 'function-graph',
        segments,
        expression: source.expression,
        from: source.from,
        to: source.to,
        scale: source.scale,
        labelAt: formulaAnchor(segments, {
          minX: box.minX * source.scale,
          maxX: box.maxX * source.scale,
          minY: box.minY * scaleY,
          maxY: box.maxY * scaleY,
          offset: source.labelOffset ?? vec.ZERO,
        }),
      };
    }

    case 'free-ellipse':
      return {
        kind: 'ellipse',
        ellipse: {
          center: source.center,
          rx: source.rx,
          ry: source.ry,
          rotation: source.rotation,
        },
      };

    case 'image':
      return {
        kind: 'image',
        position: source.position,
        width: source.width,
        height: source.height,
        href: source.href,
      };

    /* ----------------------------------------------------------------- points */

    case 'locus-point': {
      // The exactness guarantee of ADR-0003: the position is *computed from* the
      // parameter, so it lies on the host by construction rather than by convergence. That
      // holds just as exactly for one side of a polygon, which is what `edge` names.
      const host = hostCurve(parents[0], source.edge);
      if (host === null) return null;
      return finitePoint(pointAt(host, source.t));
    }

    case 'midpoint':
      return dividing(parents[0], parents[1], 0.5);

    case 'ratio-point':
      return dividing(parents[0], parents[1], source.t);

    /*
     * A solid, rebuilt from its own description every time anything about it changes.
     *
     * This is what makes the figure *live*. The first version worked the geometry out while the
     * script ran and wrote the answers into the drawing — correct once, and stale the moment a
     * corner moved. Here the corners, the hidden edges and every construction hanging off them
     * come back out of the same eight numbers, so moving or resizing the solid re-derives all
     * of it rather than leaving a picture that quietly stops matching itself.
     *
     * Nothing is drawn for this entity. Its corners and edges are separate objects, which is
     * what lets a problem name them.
     */
    case 'solid-figure': {
      const center = asPoint(parents[0]);
      if (center === null) return null;

      const placement = {
        at: center,
        width: source.width,
        height: source.height,
        depth: source.depth,
      };
      const built = buildNamedSolid(
        source.solid,
        placement,
        source.corners,
        source.foot,
        source.baseAngleAt === undefined || source.baseAngle === undefined
          ? undefined
          : {
              at: source.baseAngleAt,
              degrees: source.baseAngle,
              ...(source.baseFar === undefined ? {} : { far: source.baseFar }),
              ...(source.baseFarOffset === undefined ? {} : { offset: source.baseFarOffset }),
            },
        source.baseCorners === undefined ? undefined : ringOf(source.baseCorners),
        source.sections
      );
      if (built === null) return null;

      return {
        kind: 'solid',
        origin: center,
        visible: built.visible,
        hidden: built.hidden,
        points: built.points,
        in3d: built.in3d,
        edges: built.edges,
        extent: { width: source.width, height: source.height, depth: source.depth },
        ...(source.sections === undefined ? {} : { sections: source.sections }),
        // What a plane cut into this solid needs: the faces to clip against, and the map back.
        ...(built.faces === undefined || built.frame === undefined
          ? {}
          : { faces: built.faces, frame: built.frame }),
        ...(built.round === undefined ? {} : { round: built.round }),
        // A named cylinder, cone or sphere is round in exactly the way the shape tool's is.
        ...(built.curves === undefined ? {} : { curves: built.curves, straight: built.straight }),
      };
    }

    case 'solid-vertex': {
      const solid = parents[0];
      if (solid?.kind !== 'solid') return null;
      const at = solid.points?.[source.index];
      const space = solid.in3d?.[source.index];
      if (at === undefined || space === undefined) return null;
      return finiteSpacePoint(at, space);
    }

    /*
     * A plane, cut into the solid it belongs to.
     *
     * The value is a **polygon** rather than a kind of its own, and that is the design: a section
     * is a closed region, every painter and exporter already draws one, and hit-testing, bounds
     * and the spatial index all know what to do with it. Giving it a new kind would have meant
     * teaching sixteen files a new word to end up drawing a polygon.
     *
     * What it carries beyond an ordinary polygon is `in3d`: the section's corners *in space*.
     * That is what lets the edges running round the back be dashed, and it is what makes the
     * figure checkable — a shape whose corners are not coplanar is not a section of anything.
     */
    case 'plane': {
      const built = planeOf(parents[0], [parents[1], parents[2], parents[3]]);
      return built === null ? null : sectionValue(parents[0], built);
    }

    case 'plane-parallel': {
      const through = parents[1];
      const other = parents[2];
      if (through?.kind !== 'point' || through.in3d === undefined) return null;

      const base = planeFromSection(other);
      if (base === null) return null;

      return sectionValue(parents[0], planeParallel(through.in3d, base));
    }

    /*
     * The figure's viewpoint. Nothing is drawn; the value *is* the projection.
     *
     * Perspective is refused rather than approximated, for the reason `projectionFrame` states:
     * four points describe an **affine** map and perspective is not one, so a frame taken from it
     * would place every computed point plausibly and wrongly. `solid` makes the same refusal.
     */
    case 'space': {
      if (source.projection === ProjectionKind.Perspective) return null;
      const at = asPoint(parents[0]);
      if (at === null) return null;
      return { kind: 'space', frame: projectionFrame(at, source.rotation, source.projection) };
    }

    /*
     * A plane with nothing to cut, drawn the way a hand draws one: a leaning parallelogram.
     *
     * The value is a **polygon carrying `in3d`** — the same value `plane` returns, deliberately.
     * Everything downstream already reads that: the wash, the hit test, the exporters,
     * `planeFromSection`, `measure "angle"`, `meet`, `turn` and `dihedral`. A value kind of its
     * own would have bought nothing and cost every one of them a new word.
     *
     * `size` changes what is drawn and nothing that is true. The plane goes on forever; the
     * parallelogram is where the author chose to stop drawing it, and every angle, foot and
     * intersection comes out the same at any size.
     */
    case 'plane-free': {
      const space = parents[0];
      if (space?.kind !== 'space') return null;
      if (!Number.isFinite(source.tilt) || !(source.size > 0)) return null;

      // Level, then tipped about the frame's x axis — so two planes differing only in tilt meet
      // along that axis, which is what "hai mặt phẳng cắt nhau theo giao tuyến d" asks for.
      const normal = rotateAbout(LEVEL_NORMAL, TILT_AXIS, (source.tilt * Math.PI) / 180);
      const [u, v] = planeBasis(normal);

      const corners: readonly Vec3[] = [
        add3(scale3(u, source.size), scale3(v, source.size)),
        add3(scale3(u, -source.size), scale3(v, source.size)),
        add3(scale3(u, -source.size), scale3(v, -source.size)),
        add3(scale3(u, source.size), scale3(v, -source.size)),
      ];

      return {
        kind: 'polygon',
        vertices: corners.map((corner) => onPageOf(space.frame, corner)),
        in3d: corners,
        // Carried so that anything built on this plane — a point in it, a giao tuyến — projects
        // through the same frame it does, without being told which one twice.
        frame: space.frame,
      };
    }

    /*
     * A point on a free plane, held as coordinates in that plane's own axes.
     *
     * Recovered from the drawn parallelogram rather than recomputed from the tilt: the corners
     * already *are* the basis, scaled, so reading them back cannot drift from what the reader
     * sees. Corner 0 is `+u +v` and corner 1 is `−u +v`, so half their difference is `u·size`.
     */
    case 'plane-point': {
      const plane = parents[0];
      if (plane?.kind !== 'polygon' || plane.in3d === undefined) return null;

      const axes = planeAxesOf(plane.in3d);
      if (axes === null || plane.frame === undefined) return null;

      const at = add3(axes.centre, add3(scale3(axes.u, source.u), scale3(axes.v, source.v)));
      return finiteSpacePoint(onPageOf(plane.frame, at), at);
    }

    /*
     * A plane turned about a line in it — "mặt phẳng qua AB hợp với (ABCD) một góc 60°".
     *
     * The same value as every other plane: a polygon carrying `in3d`. Turning changes only which
     * plane is cut with, so `sectionValue` recomputes the section from the current solid and the
     * angle stays true through a rotation, a resize and a change of projection.
     *
     * Two refusals, and they are different things. The axis not lying in the plane is a *misuse*
     * — the turn would lift the plane away rather than tilt it — and the tool says so in its own
     * words. Everything else falls out of `sectionValue`, which for a **round** solid refuses
     * every oblique plane on purpose: the section is then a truncated conic that no value here
     * can express. Left unexplained that reads as the tool being broken, so the tool checks
     * whether the solid is round and says which of the two happened.
     */
    case 'plane-turn': {
      const base = planeFromSection(parents[1]);
      const a = parents[2];
      const b = parents[3];
      if (base === null) return null;
      if (a?.kind !== 'point' || a.in3d === undefined) return null;
      if (b?.kind !== 'point' || b.in3d === undefined) return null;
      if (!axisInPlane(base, a.in3d, b.in3d)) return null;

      const turned = turnPlane(base, a.in3d, b.in3d, (source.degrees * Math.PI) / 180);
      return turned === null ? null : sectionValue(parents[0], turned);
    }

    /*
     * The giao tuyến, as the piece of it inside the body.
     *
     * A **segment**, not a line, and not a kind of its own: `(SAC) ∩ (SBD)` is `SO`, the height
     * of the pyramid, and every control that already reaches a segment should reach it — dashed
     * where the solid covers it, measurable, nameable. Clipped to the solid because the line
     * itself runs off the paper, and a figure has no use for that part of it.
     */
    case 'plane-meet': {
      const a = planeFromSection(parents[1]);
      const b = planeFromSection(parents[2]);
      if (a === null || b === null) return null;

      const line = meetPlanes(a, b);
      if (line === null) return null;

      const cut = crossingOf(parents[0], parents[1], parents[2], line);
      if (cut === null) return null;

      return {
        kind: 'segment',
        segment: { a: onPageOf(cut.frame, cut.ends[0]), b: onPageOf(cut.frame, cut.ends[1]) },
        in3d: cut.ends,
      };
    }

    /*
     * The foot of the perpendicular from a point to a plane.
     *
     * A **point**, carrying its position in space like every other point built on a solid — so a
     * segment drawn to it is dashed where the body covers it, its distance can be measured, and
     * a right-angle mark at it is judged against the real angle rather than the drawn one.
     *
     * No degenerate case: a plane has a normal, and every point has exactly one nearest point on
     * it. A point already on the plane is its own foot, which is right and needs no branch.
     */
    case 'plane-foot': {
      const frame = frameOf(parents[0]);
      const plane = planeFromSection(parents[1]);
      const from = parents[2];
      if (frame === null || plane === null) return null;
      if (from?.kind !== 'point' || from.in3d === undefined) return null;

      const foot = footOnPlane(plane, from.in3d);
      return finiteSpacePoint(onPageOf(frame, foot), foot);
    }

    case 'solid-rim-point': {
      const solid = parents[0];
      if (solid?.kind !== 'solid' || solid.extent === undefined) return null;
      const found = rimPoint(
        solid.origin,
        solid.extent,
        source.ring,
        source.degrees,
        solid.sections
      );
      return found === null ? null : finiteSpacePoint(found.at, found.in3d);
    }

    /*
     * The foot of a perpendicular, worked out in space and then projected.
     *
     * `projected-point` finds it on the page, which is a different point — an affine projection
     * does not preserve perpendicularity. Measured on the cone figure the two sat 0.8716 and
     * 0.8738 of the way along the same line: invisible, and still the wrong one, and a point
     * with no position in space cannot be asked whether the solid covers the line drawn to it.
     */
    case 'solid-foot': {
      const solid = parents[0];
      const from = asPoint(parents[1]);
      const a = asPoint(parents[2]);
      const b = asPoint(parents[3]);
      if (solid?.kind !== 'solid' || from === null || a === null || b === null) return null;

      const space = [parents[1], parents[2], parents[3]].map((parent) =>
        parent?.kind === 'point' ? parent.in3d : undefined
      );
      const [fromSpace, aSpace, bSpace] = space;
      if (fromSpace === undefined || aSpace === undefined || bSpace === undefined) return null;

      const foot = perpendicularFoot(fromSpace, aSpace, bSpace);
      return foot === null ? null : finiteSpacePoint(projectOnto(solid.origin, foot), foot);
    }

    case 'segment-midpoint': {
      const curve = asCurve(parents[0]);
      if (curve?.kind !== 'segment') return null;
      return finitePoint(lines.segmentMidpoint(curve.segment));
    }

    case 'intersection': {
      const a = asCurve(parents[0]);
      const b = asCurve(parents[1]);
      if (a === null || b === null) return null;
      const hits = intersectCurves(a, b);
      const chosen = hits[source.index];
      // Fewer intersections than the index asks for is the normal degenerate case:
      // two circles pulled apart still have a valid construction, just no value.
      if (chosen === undefined) return null;

      const inSpace = crossingInSpace(parents[0], parents[1], chosen);
      return inSpace === null ? finitePoint(chosen) : finiteSpacePoint(chosen, inSpace);
    }

    case 'projected-point': {
      const point = asPoint(parents[0]);
      const onto = asCurve(parents[1]);
      if (point === null || onto === null) return null;
      return finitePoint(closestPoint(onto, point));
    }

    case 'reflected-point': {
      const point = asPoint(parents[0]);
      const mirror = parents[1];
      if (point === null) return null;

      const mirrorPoint = asPoint(mirror);
      if (mirrorPoint !== null) {
        // Reflection in a point is a half-turn about it.
        return finitePoint(vec.addScaled(mirrorPoint, vec.subtract(mirrorPoint, point), 1));
      }

      const mirrorLine = asLine(mirror);
      if (mirrorLine === null) return null;
      return finitePoint(lines.reflectAcrossLine(mirrorLine, point));
    }

    case 'rotated-point': {
      const point = asPoint(parents[0]);
      const center = asPoint(parents[1]);
      if (point === null || center === null) return null;
      return finitePoint(vec.rotateAround(point, center, source.angle));
    }

    case 'translated-point': {
      const point = asPoint(parents[0]);
      const vector = parents[1];
      if (point === null || vector?.kind !== 'vector') return null;
      return finitePoint(vec.add(point, vec.subtract(vector.to, vector.from)));
    }

    case 'scaled-point': {
      const point = asPoint(parents[0]);
      const center = asPoint(parents[1]);
      if (point === null || center === null) return null;
      return finitePoint(mat.transformPoint(mat.scalingAround(center, source.factor), point));
    }

    case 'circumcenter': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const circle = conics.circleThroughThreePoints(...triangle);
      return circle === null ? null : finitePoint(circle.center);
    }

    case 'centroid': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const [a, b, c] = triangle;
      return finitePoint({ x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 });
    }

    case 'incenter': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const [a, b, c] = triangle;
      // Weighted by the opposite side lengths — the standard barycentric form.
      const la = vec.distance(b, c);
      const lb = vec.distance(a, c);
      const lc = vec.distance(a, b);
      const total = la + lb + lc;
      if (total <= EPSILON) return null;
      return finitePoint({
        x: (la * a.x + lb * b.x + lc * c.x) / total,
        y: (la * a.y + lb * b.y + lc * c.y) / total,
      });
    }

    case 'orthocenter': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const [a, b, c] = triangle;
      // Intersection of two altitudes. Undefined for a degenerate triangle, which
      // lineLine reports by returning null.
      const altitudeA = {
        origin: a,
        direction: vec.normalize(vec.perpendicular(vec.subtract(c, b))),
      };
      const altitudeB = {
        origin: b,
        direction: vec.normalize(vec.perpendicular(vec.subtract(c, a))),
      };
      if (vec.isZero(altitudeA.direction) || vec.isZero(altitudeB.direction)) return null;
      const hit = geomIntersect.lineLine(altitudeA, altitudeB);
      return hit === null ? null : finitePoint(hit);
    }

    case 'circle-center': {
      const circle = asCircle(parents[0]);
      return circle === null ? null : finitePoint(circle.center);
    }

    /* ----------------------------------------------------------------- linear */

    case 'segment': {
      const a = asPoint(parents[0]);
      const b = asPoint(parents[1]);
      if (a === null || b === null) return null;

      // Passed on when both ends have it, exactly as `midpoint` passes it on: the segment is
      // then a segment *in space* that happens to be drawn on a page, and `intersect` can say
      // where a crossing really is rather than only where it appears.
      const from = parents[0]?.kind === 'point' ? parents[0].in3d : undefined;
      const to = parents[1]?.kind === 'point' ? parents[1].in3d : undefined;

      return from === undefined || to === undefined
        ? { kind: 'segment', segment: { a, b } }
        : { kind: 'segment', segment: { a, b }, in3d: [from, to] };
    }

    case 'vector': {
      const from = asPoint(parents[0]);
      const to = asPoint(parents[1]);
      if (from === null || to === null) return null;
      return { kind: 'vector', from, to };
    }

    case 'ray': {
      const origin = asPoint(parents[0]);
      const through = asPoint(parents[1]);
      if (origin === null || through === null) return null;
      const direction = vec.normalize(vec.subtract(through, origin));
      if (vec.isZero(direction)) return null;
      return { kind: 'ray', ray: { origin, direction } };
    }

    case 'line': {
      const a = asPoint(parents[0]);
      const b = asPoint(parents[1]);
      if (a === null || b === null) return null;
      const direction = vec.normalize(vec.subtract(b, a));
      if (vec.isZero(direction)) return null;
      return { kind: 'line', line: { origin: a, direction } };
    }

    case 'parallel-line': {
      const reference = asLine(parents[0]);
      const through = asPoint(parents[1]);
      if (reference === null || through === null) return null;
      return { kind: 'line', line: lines.parallelThrough(reference, through) };
    }

    case 'perpendicular-line': {
      const reference = asLine(parents[0]);
      const through = asPoint(parents[1]);
      if (reference === null || through === null) return null;
      return { kind: 'line', line: lines.perpendicularThrough(reference, through) };
    }

    case 'perpendicular-bisector': {
      const a = asPoint(parents[0]);
      const b = asPoint(parents[1]);
      if (a === null || b === null) return null;
      const bisector = lines.perpendicularBisector({ a, b });
      return bisector === null ? null : { kind: 'line', line: bisector };
    }

    case 'angle-bisector': {
      const a = asPoint(parents[0]);
      const vertex = asPoint(parents[1]);
      const c = asPoint(parents[2]);
      if (a === null || vertex === null || c === null) return null;

      const toA = vec.normalize(vec.subtract(a, vertex));
      const toC = vec.normalize(vec.subtract(c, vertex));
      if (vec.isZero(toA) || vec.isZero(toC)) return null;

      // Summing the two unit directions bisects the angle. It degenerates only when the
      // arms are exactly opposite, where "the bisector" is genuinely ambiguous; the
      // perpendicular is the conventional choice.
      const sum = vec.add(toA, toC);
      const direction = vec.isZero(sum, 1e-9) ? vec.perpendicular(toA) : vec.normalize(sum);
      return { kind: 'ray', ray: { origin: vertex, direction } };
    }

    case 'tangent-line': {
      const from = asPoint(parents[1]);
      const circle = asCircle(parents[0]);
      if (from === null || circle === null) return null;

      // Tangent points lie on the circle whose diameter is centre-to-external-point:
      // the tangent-radius right angle is inscribed in it (Thales).
      const helper = {
        center: vec.midpoint(circle.center, from),
        radius: vec.distance(circle.center, from) / 2,
      };
      const touchPoints = geomIntersect.circleCircle(circle, helper);
      const touch = touchPoints[source.index];
      if (touch === undefined) return null;

      const direction = vec.normalize(vec.subtract(touch, from));
      if (vec.isZero(direction)) return null;
      return { kind: 'line', line: { origin: from, direction } };
    }

    /*
     * The tangent to a curve at a given abscissa — "tiếp tuyến của (C) tại điểm có hoành độ x₀".
     *
     * Differentiated symbolically rather than by a difference quotient, for the reason
     * `derivative.ts` was written: near a turning point `(f(x+h) − f(x−h)) / 2h` is a small
     * difference of two nearly equal numbers, and the slope a student is asked to compute is
     * exactly where that arithmetic is worst. A tangent drawn a degree off at a maximum is a
     * figure that contradicts the answer it is illustrating.
     *
     * The mathematical slope is the world slope unchanged, and that is a fact about this editor
     * rather than an accident: `FunctionGraph.scale` is applied to **both axes equally**, so a
     * gradient of 3 on the page really is a gradient of 3. If the two scales were ever allowed
     * to differ, this line is where the figure would start lying.
     *
     * A vertical tangent — `√x` at 0 — gives an infinite gradient, so the value comes out
     * non-finite and the last gate in `evaluate` turns it into "no such construction". The line
     * disappears and returns if the abscissa is moved, which is how every degenerate case in
     * this file behaves.
     */
    case 'graph-tangent': {
      const curve = parents[0];
      if (curve?.kind !== 'function-graph') return null;

      let parsed;
      try {
        parsed = parseExpression(curve.expression);
      } catch {
        return null;
      }

      const derivative = differentiate(parsed);
      if (derivative === null) return null;

      const height = evaluateAt(parsed, source.at);
      const gradient = evaluateAt(derivative, source.at);
      if (height === null || gradient === null) return null;

      return {
        kind: 'line',
        line: {
          origin: vec.vec2(source.at * curve.scale, height * curve.scale),
          direction: vec.normalize(vec.vec2(1, gradient)),
        },
      };
    }

    /* ----------------------------------------------------------------- conics */

    case 'circle-through': {
      const center = asPoint(parents[0]);
      const through = asPoint(parents[1]);
      if (center === null || through === null) return null;
      return { kind: 'circle', circle: { center, radius: vec.distance(center, through) } };
    }

    case 'circle-radius': {
      const center = asPoint(parents[0]);
      if (center === null) return null;
      return { kind: 'circle', circle: { center, radius: Math.abs(source.radius) } };
    }

    case 'circle-3-points': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const circle = conics.circleThroughThreePoints(...triangle);
      return circle === null ? null : { kind: 'circle', circle };
    }

    case 'arc-3-points': {
      const triangle = readTriangle(parents);
      if (triangle === null) return null;
      const [a, b, c] = triangle;
      const circle = conics.circleThroughThreePoints(a, b, c);
      if (circle === null) return null;

      const startAngle = conics.angleOnCircle(circle, a);
      const endAngle = conics.angleOnCircle(circle, c);
      // `b` decides which of the two arcs is meant. Its orientation relative to a->c
      // tells us the sweep direction.
      const counterclockwise = vec.cross(vec.subtract(b, a), vec.subtract(c, a)) < 0;

      return {
        kind: 'arc',
        arc: {
          center: circle.center,
          radius: circle.radius,
          startAngle,
          endAngle,
          counterclockwise,
        },
      };
    }

    case 'arc-center': {
      const center = asPoint(parents[0]);
      const start = asPoint(parents[1]);
      const end = asPoint(parents[2]);
      if (center === null || start === null || end === null) return null;

      const radius = vec.distance(center, start);
      if (radius <= EPSILON) return null;

      return {
        kind: 'arc',
        arc: {
          center,
          radius,
          startAngle: vec.angle(vec.subtract(start, center)),
          // The end point only fixes the *direction*; the radius comes from the start,
          // which is what keeps the arc circular when the two are dragged apart.
          endAngle: vec.angle(vec.subtract(end, center)),
          counterclockwise: source.counterclockwise,
        },
      };
    }

    case 'ellipse': {
      const center = asPoint(parents[0]);
      if (center === null) return null;
      return {
        kind: 'ellipse',
        ellipse: { center, rx: source.rx, ry: source.ry, rotation: source.rotation },
      };
    }

    case 'ellipse-foci': {
      const f1 = asPoint(parents[0]);
      const f2 = asPoint(parents[1]);
      const through = asPoint(parents[2]);
      if (f1 === null || f2 === null || through === null) return null;

      // The string-and-two-pins definition: 2a is the constant focal sum.
      const focalSum = vec.distance(through, f1) + vec.distance(through, f2);
      const focalDistance = vec.distance(f1, f2);
      const a = focalSum / 2;
      const cDist = focalDistance / 2;
      if (a <= cDist + EPSILON) return null; // degenerate: the point is on the segment

      return {
        kind: 'ellipse',
        ellipse: {
          center: vec.midpoint(f1, f2),
          rx: a,
          ry: Math.sqrt(a * a - cDist * cDist),
          rotation: vec.angle(vec.subtract(f2, f1)),
        },
      };
    }

    /* --------------------------------------------------------------- polygons */

    case 'polygon': {
      const vertices: Vec2[] = [];
      for (const parent of parents) {
        const point = asPoint(parent);
        if (point === null) return null;
        vertices.push(point);
      }
      if (vertices.length < 3) return null;
      return { kind: 'polygon', vertices };
    }

    case 'solid': {
      const anchor = asPoint(parents[0]);
      if (anchor === null) return null;

      const wire = projectSolid({
        parameters: {
          kind: source.solid,
          width: source.width,
          height: source.height,
          depth: source.depth,
          sides: source.sides,
          taper: source.taper,
        },
        rotation: source.rotation,
        projection: source.projection,
        origin: anchor,
      });

      return {
        kind: 'solid',
        origin: anchor,
        visible: wire.visible,
        hidden: wire.hidden,
        ...(wire.curves === undefined ? {} : { curves: wire.curves, straight: wire.straight }),
        /*
         * A cube dropped from the shape panel gets its corners numbered too, so one of its edges
         * can be lifted out and drawn in its own colour. Its *kind* stays what it was: the point
         * of the two solids is that one is a picture and the other is a figure a problem names,
         * and turning the first into the second would drop the rotation and the projection the
         * user chose, which neither `solid-figure` carries.
         */
        ...(wire.corners === undefined
          ? {}
          : {
              points: wire.corners.points,
              in3d: wire.corners.in3d,
              edges: wire.corners.edges,
            }),
        /*
         * What a plane cut into this solid needs: the faces to clip against, and the map back to
         * the page. `solid-figure` has forwarded both since planes existed; this one dropped them,
         * and the effect was that **no plane could be cut into a solid placed from the toolbar** —
         * `cuttable` asks for exactly this pair. Clicking three corners of a toolbar cube with the
         * plane tool refused with the generic "không dựng được với lựa chọn này", which reads as
         * the three corners being wrong when nothing about them was.
         *
         * Not under perspective. `frameOf` says so in as many words — four points settle an
         * *affine* map, and a perspective projection is not one, so a frame taken from it would
         * place every computed point wrongly while looking entirely plausible. Withholding it is
         * how "the caller is responsible for not asking" is honoured, and a section is then
         * refused rather than drawn in the wrong place.
         */
        ...(wire.corners === undefined || source.projection === ProjectionKind.Perspective
          ? {}
          : { faces: wire.corners.faces, frame: wire.corners.frame }),
        ...(wire.round === undefined || source.projection === ProjectionKind.Perspective
          ? {}
          : { round: wire.round }),
      };
    }

    case 'polygon-boolean': {
      const first = asVertices(parents[0]);
      const second = asVertices(parents[1]);
      if (first === null || second === null) return null;

      const rings = booleanOps.polygonBoolean(first, second, source.operation);
      /*
       * Only a single-ring result can be represented. `Evaluated` carries one ring per
       * polygon, so a result with a hole or in two disjoint pieces has nowhere to live;
       * returning the largest ring would quietly discard part of the answer, and an
       * undefined construction is the established way to say "not right now".
       */
      const only = rings.length === 1 ? rings[0] : undefined;
      return only === undefined || only.length < 3 ? null : { kind: 'polygon', vertices: only };
    }

    case 'regular-polygon': {
      const center = asPoint(parents[0]);
      const vertex = asPoint(parents[1]);
      if (center === null || vertex === null) return null;

      const radius = vec.distance(center, vertex);
      if (radius <= EPSILON) return null;

      const startAngle = vec.angle(vec.subtract(vertex, center));
      return {
        kind: 'polygon',
        vertices: polygonOps.regularPolygon(center, radius, source.sides, startAngle),
      };
    }

    /* ------------------------------------------------------------- annotation */

    case 'text': {
      const anchor = source.anchor === null ? null : anchorPosition(parents[0]);
      if (source.anchor !== null && anchor === null) return null;
      /*
       * A caption on a segment steps off it, square to its direction.
       *
       * Only here, and only for a segment. The anchor itself stays the midpoint, because that is
       * the right answer for everything that asks it — a name placed by `nameDirections`, which
       * probes outward from there for somewhere free of ink, and a measurement, which carries an
       * offset of its own. What a *caption* needs is the one thing the midpoint cannot give: to
       * be beside the line rather than across it.
       *
       * Skipped when the author has said where they want it. An offset that was typed is an
       * instruction, and adding a nudge of our own to it would mean the same script drew
       * differently depending on a rule nobody could see.
       */
      const beside =
        anchor !== null && parents[0]?.kind === 'segment' && vec.length(source.offset) < EPSILON
          ? sideStep(parents[0].segment)
          : vec.ZERO;
      const position =
        anchor === null ? source.offset : vec.add(anchor, vec.add(source.offset, beside));
      return { kind: 'text', position, content: source.content, latex: source.latex };
    }

    case 'angle-mark': {
      const vertex = asPoint(parents[1]);
      if (vertex === null) return null;

      /*
       * An arm may be given as a *point* or as the *side itself*.
       *
       * The first version took three points, which matched the maths and not the user: the
       * thing a person points at when they say "this angle" is the two sides. Hovering along
       * a side found nothing, because the interior of a segment holds no point entity —
       * so the tool refused the one gesture everybody tries first.
       */
      const first = armFrom(parents[0], vertex);
      const second = armFrom(parents[2], vertex);
      if (first === null || second === null) return null;

      /*
       * Measured in space when the figure has one, and on the page when it does not.
       *
       * The wedge drawn between the arms is a *shadow* of the angle whenever the points came
       * from a solid, and its size says more about how the solid is turned than about the
       * figure. The number beside it has to be the real one, or a mark on a cube reads 60° for
       * a right angle and the drawing contradicts itself.
       */
      const degrees =
        spaceAngleOf(parents[0], parents[1], parents[2]) ??
        angle.toDegrees(Math.abs(vec.angleBetween(first, second)));

      /*
       * Squareness is decided here, once, rather than at paint time — so the canvas, an SVG
       * and a PDF can never disagree about whether an angle is right.
       *
       * The tolerance is in *degrees* rather than on the dot product. A dot-product epsilon
       * behaves differently depending on how long the arms are, which would make a mark
       * appear on a short angle and not on the same angle drawn larger. A tenth of a degree
       * is below what any drawing can express and well above floating-point noise.
       */
      /*
       * A caller that knows the solid overrules the measurement, because a projection is not
       * required to preserve the angle it was asked about — see `right` on the source. Nothing
       * else may: the flag is absent for every construction made by hand, and then the drawing
       * decides, which is correct for a figure that lies in the plane.
       */
      const isRight =
        squareOf(parents[0], parents[1], parents[2]) ??
        source.right ??
        Math.abs(degrees - 90) < RIGHT_ANGLE_TOLERANCE_DEGREES;

      return {
        kind: 'angle-mark',
        vertex,
        from: vec.normalize(first),
        to: vec.normalize(second),
        degrees,
        isRight,
        ticks: Math.max(1, Math.min(3, Math.round(source.ticks))),
        // Clamped: the arc has to stay a mark rather than become a circle round the figure,
        // and a mis-typed number must not be able to swallow the drawing.
        size: Math.min(Math.max(source.size ?? 1, 0.5), 4),
        showValue: source.showValue,
        valueOffset: source.valueOffset ?? vec.ZERO,
        // The mark has to fit inside the angle, and only the geometry knows how much room
        // there is. The shorter arm is the constraint.
        armLength: Math.min(vec.length(first), vec.length(second)),
      };
    }

    /*
     * The dihedral angle, marked where a book marks it.
     *
     * ## The construction, which is the point of the whole thing
     *
     * Not the angle between two edges. Two planes meet in a line; take any point `M` on it, draw
     * from `M` the ray in each plane that is **perpendicular to that line**, and the angle between
     * those two rays is the dihedral. Every other pair of rays gives a different, wrong number,
     * which is exactly why a student is taught to find these ones — and why a mark placed by hand
     * is right only when the author had already solved it.
     *
     * `M` is the middle of the chord inside the body, so the arc lands in the figure rather than
     * off the end of it. The two directions are the components of "towards this section's middle"
     * with the line's own direction taken out, which is that perpendicular and also points *into*
     * each section rather than out of the figure.
     *
     * ## Why the drawn arc and the written number disagree, correctly
     *
     * The arc is drawn between the two directions **as projected**, because that is where the two
     * faces appear to be and a mark anywhere else would not read as belonging to them. The number
     * is the angle **in space**, because that is the answer. A projection is free to change the
     * first and must never change the second — the same split `angle-mark` already makes for a
     * mark on a solid, and for the same reason.
     */
    case 'dihedral-mark': {
      const first = planeFromSection(parents[1]);
      const second = planeFromSection(parents[2]);
      if (first === null || second === null) return null;

      const line = meetPlanes(first, second);
      if (line === null) return null;

      const cut = crossingOf(parents[0], parents[1], parents[2], line);
      if (cut === null) return null;
      const { frame, ends: chord } = cut;

      const at = scale3(add3(chord[0], chord[1]), 0.5);
      const armA = armAcross(line.direction, at, parents[1]);
      const armB = armAcross(line.direction, at, parents[2]);
      if (armA === null || armB === null) return null;

      const vertex = onPageOf(frame, at);
      const drawn = (arm: Vec3): Vec2 => {
        const tip = onPageOf(frame, add3(at, arm));
        return vec.vec2(tip.x - vertex.x, tip.y - vertex.y);
      };

      const onPageA = drawn(armA);
      const onPageB = drawn(armB);
      if (vec.isZero(onPageA) || vec.isZero(onPageB)) return null;

      const degrees = angle.toDegrees(angleBetween(first, second));

      return {
        kind: 'angle-mark',
        vertex,
        from: vec.normalize(onPageA),
        to: vec.normalize(onPageB),
        degrees,
        isRight: Math.abs(degrees - 90) < RIGHT_ANGLE_TOLERANCE_DEGREES,
        ticks: Math.max(1, Math.min(3, Math.round(source.ticks))),
        size: Math.min(Math.max(source.size ?? 1, 0.5), 4),
        showValue: source.showValue,
        valueOffset: source.valueOffset ?? vec.ZERO,
        armLength: Math.min(vec.length(onPageA), vec.length(onPageB)),
      };
    }

    case 'measurement': {
      const value = measure(source.measure, parents);
      if (value === null) return null;
      const anchor = anchorPosition(parents[0]) ?? vec.ZERO;
      return { kind: 'measurement', position: vec.add(anchor, source.offset), value };
    }
  }
}

/* -------------------------------------------------------------------- helpers */

function readTriangle(parents: readonly (Evaluated | null)[]): readonly [Vec2, Vec2, Vec2] | null {
  const a = asPoint(parents[0]);
  const b = asPoint(parents[1]);
  const c = asPoint(parents[2]);
  if (a === null || b === null || c === null) return null;
  return [a, b, c];
}

/**
 * A representative position for any value, used to place labels and measurements.
 *
 * Chosen to be where a reader would naturally expect the label: the midpoint of a
 * segment, the centre of a circle, the area centroid of a polygon.
 */
/**
 * The direction of one arm of an angle, from the vertex.
 *
 * Accepts whichever way the user named it:
 *
 * - a **point** — the arm runs from the vertex to it;
 * - a **segment or vector** — the arm runs to whichever end is further from the vertex,
 *   which is the other end whenever the vertex is one of them.
 *
 * Returns `null` when the arm does not actually meet the vertex. That is the honest answer
 * and it is also the useful one: the tool previews what it would create, so a side picked
 * from the wrong corner of the figure shows nothing and the user sees the mistake before
 * committing to it. Inventing a direction would produce a mark that means nothing.
 *
 * Endless arms — a line or a ray — are not accepted. A line offers two opposite directions
 * with nothing to choose between them, and picking one would be a guess.
 */
function armFrom(value: Evaluated | null | undefined, vertex: Vec2): Vec2 | null {
  if (value == null) return null;
  // Neither a plotted curve, a frame of reference, a table nor a heap of marks has a direction
  // to be an arm.
  if (value.kind === 'function-graph' || value.kind === 'axes') return null;
  if (value.kind === 'variation-table' || value.kind === 'graph-marks') return null;
  // Nor does a viewpoint, which has no geometry at all.
  if (value.kind === 'space') return null;

  const ends = (a: Vec2, b: Vec2): Vec2 | null => {
    // The vertex has to lie on the side, or the two do not form an angle at all.
    const span = vec.distance(a, b);
    if (span <= EPSILON) return null;

    /*
     * The allowance has an absolute floor as well as a proportional part.
     *
     * A purely proportional rule is strict exactly where it should be lax: a 40-unit side
     * allows 0.8 units, so two corner points a few units apart — which is what imprecise
     * drawing without snapping produces, and which look identical on screen — are judged not
     * to meet. The user then sees the side they are pointing at refused for no visible
     * reason, while some *other* side that happens to pass through the point they actually
     * picked is accepted.
     *
     * The floor is in world units and comparable to a click's own precision, because that is
     * the precision at which the user identified both things in the first place.
     */
    const allowance = Math.max(span * ARM_ATTACH_TOLERANCE, ARM_ATTACH_FLOOR);
    if (lines.distanceToSegment({ a, b }, vertex) > allowance + EPSILON) return null;

    const far = vec.distance(a, vertex) >= vec.distance(b, vertex) ? a : b;
    const direction = vec.subtract(far, vertex);
    return vec.isZero(direction) ? null : direction;
  };

  switch (value.kind) {
    case 'point': {
      const direction = vec.subtract(value.point, vertex);
      return vec.isZero(direction) ? null : direction;
    }

    case 'segment':
      return ends(value.segment.a, value.segment.b);

    case 'vector':
      return ends(value.from, value.to);

    /*
     * Everything else, and for two different reasons.
     *
     * An endless arm is refused rather than guessed at: a line offers two opposite directions
     * from the vertex with nothing to choose between them, and a ray's own direction may run
     * away from the vertex rather than out from it.
     *
     * The rest are not arms of anything — they enclose or annotate rather than emanate.
     *
     * (A comment cannot sit between two `case` labels here; the lint reads the gap as a
     * fallthrough, so the whole run is explained in one place.)
     */
    case 'ray':
    case 'line':
    case 'circle':
    case 'arc':
    case 'ellipse':
    case 'polygon':
    case 'polyline':
    case 'solid':
    case 'text':
    case 'measurement':
    case 'angle-mark':
    case 'image':
      return null;
  }
}

/**
 * How far off a side the vertex may sit and still count as being on it.
 *
 * A fraction of the side's own length rather than an absolute distance, so the rule reads
 * the same on a 20-unit triangle and a 2000-unit one. Two per cent is well inside what a
 * click can express and far outside the drift of a constructed intersection.
 */
const ARM_ATTACH_TOLERANCE = 0.03;

/**
 * The smallest allowance, in world units, whatever the side's length.
 *
 * Roughly a click's own precision at ordinary zoom. Without it the rule is tightest on short
 * sides, which is backwards: a user clicks at the same precision regardless of how long the
 * thing they are clicking happens to be.
 */
const ARM_ATTACH_FLOOR = 4;

/**
 * Which way a caption steps off its segment, and how far.
 *
 * The left normal, chosen because it has to be *some* fixed rule and a fixed rule is what makes
 * two crossing segments send their captions apart: the offset is a function of the line's own
 * direction, so lines that differ in direction differ here too. Reversing the ends of a segment
 * flips it, which is the price of not having a side to prefer — a figure has no up.
 */
/* ---------------------------------------------------------------------------- planes */

/**
 * The solid's own numbering, when it has one.
 *
 * Absent for a sphere or a cylinder, whose drawn lines are silhouettes rather than edges of the
 * object, and absent under perspective, where the projection is not affine and a frame of four
 * points would be a lie. A plane on either simply has no value, and the figure shows nothing —
 * the same answer every other construction gives when its inputs do not define it.
 */
function frameOf(solid: Evaluated | null | undefined): SolidFrame | null {
  return solid?.kind === 'solid' ? (solid.frame ?? null) : null;
}

/**
 * Whether this solid can be cut at all.
 *
 * A polyhedron is cut from its mesh and needs `frame` and `faces`; a round one is cut from its
 * radii and carries its own frame beside them. Asking for the mesh's frame alone was the first
 * version, and it refused every cylinder — the guard has to be "can this be cut", not "is this
 * the kind of solid the first implementation handled".
 */
function cuttable(solid: Evaluated | null | undefined): boolean {
  if (solid?.kind !== 'solid') return false;
  return solid.round !== undefined || (solid.frame !== undefined && solid.faces !== undefined);
}

/** The solid as the mesh in space that `sectionOf` and `chordOf` work on. */
function meshOf(solid: Evaluated | null | undefined): {
  vertices: readonly Vec3[];
  faces: readonly (readonly number[])[];
  edges: readonly { a: number; b: number }[];
} | null {
  if (solid?.kind !== 'solid') return null;
  const { in3d, edges, faces } = solid;
  if (in3d === undefined || edges === undefined || faces === undefined) return null;

  return { vertices: in3d, faces, edges: edges.map(([a, b]) => ({ a, b })) };
}

/** The plane through three of the figure's points, all of which must know where they are. */
function planeOf(
  solid: Evaluated | null | undefined,
  points: readonly (Evaluated | null | undefined)[]
): Plane | null {
  if (!cuttable(solid)) return null;

  const space = points.map((point) => (point?.kind === 'point' ? point.in3d : undefined));
  const [a, b, c] = space;
  // A point with no position in space is a mark on the page; three of those name no plane.
  if (a === undefined || b === undefined || c === undefined) return null;

  return planeThrough(a, b, c);
}

/**
 * The ray a dihedral mark is drawn along, in one of its two planes.
 *
 * Perpendicular to the shared line, pointing **into** the face, and as long as the face is wide
 * — so the arc is proportioned to the section it sits in rather than to a constant that would
 * overflow a small one and disappear inside a large one.
 *
 * ## Why the farthest corner and not the centroid
 *
 * The centroid was the first version and it fails on the commonest figure in the book. `(SAC)`
 * and `(SBD)` in a square pyramid meet along the height; each section is a triangle whose apex is
 * `S` and whose base is a diagonal through the centre, so its centroid lies **on that height** —
 * the perpendicular component is zero and the mark refuses. Nothing is degenerate about the
 * figure; it is symmetric, which is why it is in every exercise.
 *
 * Taking the corner that stands farthest off the line cannot collapse that way: a face with every
 * corner on the line is not a face. It also aims at the widest part of the section, which is where
 * an arc has room.
 */
function armAcross(along: Vec3, at: Vec3, section: Evaluated | null | undefined): Vec3 | null {
  if (section?.kind !== 'polygon' || section.in3d === undefined) return null;

  let best: Vec3 | null = null;
  let reach = EPSILON;

  for (const corner of section.in3d) {
    const toward = { x: corner.x - at.x, y: corner.y - at.y, z: corner.z - at.z };
    const overlap = toward.x * along.x + toward.y * along.y + toward.z * along.z;
    const across = {
      x: toward.x - along.x * overlap,
      y: toward.y - along.y * overlap,
      z: toward.z - along.z * overlap,
    };

    const size = Math.hypot(across.x, across.y, across.z);
    if (size > reach) {
      reach = size;
      best = across;
    }
  }

  return best;
}

/** A level plane's normal, and the frame axis a tilt turns about. */
const LEVEL_NORMAL: Vec3 = { x: 0, y: 1, z: 0 };
const TILT_AXIS: Vec3 = { x: 1, y: 0, z: 0 };

/**
 * The piece of a line two planes share, and the frame to draw it through.
 *
 * Two figures ask this question and the answer differs by what there is to be *inside* of.
 *
 * - A **solid** figure clips the line to the body, because `(SAC) ∩ (SBD)` is `SO` — the height
 *   of the pyramid — and the line itself runs off the paper.
 * - A **blackboard** figure has no body. The two planes are drawn as parallelograms, and the line
 *   a reader takes to be the giao tuyến is the piece inside *both* of them, which is what a hand
 *   draws across the crossing.
 *
 * Chosen by what the first parent is, so neither path is a special case of the other and
 * `chordOf` — proved, and about half-spaces — is left exactly as it was.
 */
function crossingOf(
  host: Evaluated | null | undefined,
  first: Evaluated | null | undefined,
  second: Evaluated | null | undefined,
  line: { origin: Vec3; direction: Vec3 }
): { frame: SolidFrame; ends: readonly [Vec3, Vec3] } | null {
  const solidFrame = frameOf(host);
  const mesh = meshOf(host);
  if (solidFrame !== null && mesh !== null) {
    const chord = chordOf(mesh, line);
    return chord === null ? null : { frame: solidFrame, ends: chord };
  }

  // A blackboard figure: the frame comes from the planes themselves, which is where a free plane
  // carries it precisely so that nothing built on one has to be told which viewpoint twice.
  const frame =
    first?.kind === 'polygon' ? first.frame : second?.kind === 'polygon' ? second.frame : undefined;
  if (frame === undefined) return null;
  if (first?.kind !== 'polygon' || second?.kind !== 'polygon') return null;
  if (first.in3d === undefined || second.in3d === undefined) return null;

  const insideFirst = chordInRing(first.in3d, line);
  const insideSecond = chordInRing(second.in3d, line);
  if (insideFirst === null || insideSecond === null) return null;

  const low = Math.max(insideFirst[0], insideSecond[0]);
  const high = Math.min(insideFirst[1], insideSecond[1]);
  if (!(low < high)) return null;

  const at = (t: number): Vec3 => add3(line.origin, scale3(line.direction, t));
  return { frame, ends: [at(low), at(high)] };
}

/**
 * A free plane's own axes, read back off the parallelogram it is drawn as.
 *
 * Recovered from the corners rather than recomputed from the tilt, so a point placed in a plane
 * cannot drift from the shape a reader is looking at — there is one description of where the
 * plane is, and it is the one on screen. The corners are written `+u+v`, `−u+v`, `−u−v`, `+u−v`,
 * so half of `corner0 − corner1` is `u` scaled by the half-width, and the same for `v`.
 *
 * Unit vectors and the half-width kept apart, because `(u, v)` on a `plane-point` are **world
 * distances** rather than fractions: a point stays where it was when the author draws the plane
 * larger, which is what makes `size` a matter of appearance alone.
 */
function planeAxesOf(
  corners: readonly Vec3[]
): { centre: Vec3; u: Vec3; v: Vec3; half: number } | null {
  const [a, b, , d] = corners;
  if (corners.length !== 4 || a === undefined || b === undefined || d === undefined) return null;

  const acrossU = scale3(sub3(a, b), 0.5);
  const acrossV = scale3(sub3(a, d), 0.5);
  const half = length3(acrossU);
  if (half < EPSILON || length3(acrossV) < EPSILON) return null;

  let centre: Vec3 = { x: 0, y: 0, z: 0 };
  for (const corner of corners) centre = add3(centre, corner);

  return {
    centre: scale3(centre, 1 / corners.length),
    u: scale3(acrossU, 1 / half),
    v: normalize3(acrossV),
    half,
  };
}

/**
 * The plane a section lies in, recovered from the section itself.
 *
 * So that `plane-meet` and `plane-parallel` can take a *plane entity* as a parent without a
 * second value kind existing only to carry a normal. A section has at least three corners in
 * space, and three corners are a plane.
 */
function planeFromSection(value: Evaluated | null | undefined): Plane | null {
  if (value?.kind !== 'polygon' || value.in3d === undefined) return null;

  const [first, second] = value.in3d;
  if (first === undefined || second === undefined) return null;

  for (const point of value.in3d.slice(2)) {
    const plane = planeThrough(first, second, point);
    if (plane !== null) return plane;
  }
  return null;
}

/**
 * The section of the solid by the plane, as the shape a reader sees.
 *
 * A **round** solid is answered first and exactly, not from its mesh. Its section square to the
 * axis is a *circle*, and a circle under a parallel projection is an ellipse — so it is drawn as
 * one, smooth at any magnification. Cutting the tessellation instead would give the very chain of
 * chords the rim work was about, and it would be a section of a forty-eight-sided prism rather
 * than of a cylinder.
 */
function sectionValue(solid: Evaluated | null | undefined, plane: Plane): Evaluated | null {
  const round = solid?.kind === 'solid' ? solid.round : undefined;
  if (round !== undefined) return roundSectionValue(round, plane);

  const frame = frameOf(solid);
  const mesh = meshOf(solid);
  if (frame === null || mesh === null) return null;

  const space = sectionOf(mesh, plane);
  // Fewer than three corners means the plane misses the solid or only grazes a corner of it.
  // There is nothing to shade, and shading nothing would be a mark with no meaning.
  if (space.length < 3) return null;

  return {
    kind: 'polygon',
    vertices: space.map((point) => onPageOf(frame, point)),
    in3d: space,
  };
}

/**
 * The section of a round solid: an exact ellipse, or the polygon of an axial cut.
 *
 * Two answers and no third. A plane square to the axis cuts a circle; a plane through the axis
 * cuts a triangle, a rectangle or a trapezium. Anything between gives a conic truncated by the
 * flat ends — a boundary of mixed arcs and chords that no value here can express — and is
 * refused rather than sampled into a polygon that would draw it flat, convincing and wrong.
 */
function roundSectionValue(
  round: { body: RoundBody; frame: SolidFrame },
  plane: Plane
): Evaluated | null {
  const circle = roundSection(round.body, plane);
  if (circle !== null) {
    const centre = onPageOf(round.frame, circle.centre);
    const spoke = (step: Vec3): Vec2 => {
      const tip = onPageOf(round.frame, add3(circle.centre, step));
      return { x: tip.x - centre.x, y: tip.y - centre.y };
    };

    // The two radii are perpendicular in space and are not on the page; `ellipseFromDiameters`
    // is what turns a pair of conjugate half-diameters into the axes an ellipse is written with.
    const built = ellipseFromDiameters(centre, spoke(circle.u), spoke(circle.v));
    return built === null ? null : { kind: 'ellipse', ellipse: built.ellipse };
  }

  const axial = axialSection(round.body, plane);
  if (axial === null) return null;

  return {
    kind: 'polygon',
    vertices: axial.map((point) => onPageOf(round.frame, point)),
    in3d: axial,
  };
}

function sideStep(segment: Segment): Vec2 {
  const along = vec.subtract(segment.b, segment.a);
  const length = vec.length(along);
  if (length < EPSILON) return vec.ZERO;
  return vec.scale(vec.vec2(-along.y / length, along.x / length), SEGMENT_CAPTION_GAP);
}

export function anchorPosition(value: Evaluated | null | undefined): Vec2 | null {
  if (value == null) return null;
  switch (value.kind) {
    // Nothing is drawn for a viewpoint, so there is nowhere for a name to sit.
    case 'space':
      return null;

    // The origin, which is the one place a name for a pair of axes belongs.
    case 'axes':
      return value.origin;

    // The first point of the curve, which is where a formula label reads naturally — beside
    // the left end of the graph rather than floating at its centre.
    case 'function-graph':
      return value.segments[0]?.[0] ?? null;

    // The corner it hangs from, so a name for the table sits above it rather than across a row.
    case 'variation-table':
      return value.corner;

    // The first dot, so a name given to the whole group lands on something rather than at the
    // origin. A group with nothing in it — every kind unticked — has no anchor and no name.
    case 'graph-marks':
      return value.points[0] ?? null;

    /*
     * Out along the bisector, where the mark itself is, and not at the vertex.
     *
     * The vertex was the obvious answer and it is the one place the caption must not go: the
     * vertex already carries a point, usually a named one, so `text phi "φ"` landed on top of
     * both. A figure of "hình chóp S.ABCD" had its φ printed across the S.
     *
     * The arc has *two* limits and the caption has to respect both, which is the fix to a real
     * defect. It is drawn at {@link ANGLE_MARK_NOMINAL_RADIUS}, or at
     * {@link ANGLE_MARK_ARM_FRACTION} of the shorter arm when the arm is too short for that —
     * whichever is smaller. Only the second was applied here, so on a figure with long arms the
     * caption went to a third of the arm while the arc stayed at the nominal size: measured on a
     * pyramid, an `α` 360 units out from an arc of radius 22.
     *
     * The gap past the arc is added for the same reason the painter adds it: a caption sitting
     * exactly on its own arc is a caption with a line through it.
     */
    case 'angle-mark': {
      const bisector = vec.add(value.from, value.to);
      const spread = vec.length(bisector);
      // Arms exactly opposite: no inside to the corner, and nowhere better than the vertex.
      if (spread < 1e-9) return value.vertex;

      const radius = angleMarkOuterRadius(
        value.armLength,
        value.size,
        value.showValue,
        value.ticks
      );
      return vec.add(value.vertex, vec.scale(bisector, (radius + ANGLE_MARK_LABEL_GAP) / spread));
    }

    // A solid's label belongs at the point it hangs from, not at the wireframe's centre —
    // the two differ under perspective, and the anchor is the thing the user placed.
    case 'solid':
      return value.origin;

    case 'point':
      return value.point;
    case 'segment':
      return lines.segmentMidpoint(value.segment);
    case 'vector':
      return vec.midpoint(value.from, value.to);
    case 'ray':
      return value.ray.origin;
    case 'line':
      return value.line.origin;
    case 'circle':
      return value.circle.center;
    case 'arc':
      return conics.arcMidpoint(value.arc);
    case 'ellipse':
      return value.ellipse.center;
    case 'polygon':
      return polygonOps.centroid(value.vertices);
    case 'polyline':
      return value.points[0] ?? null;
    case 'text':
    case 'measurement':
    case 'image':
      return value.position;
  }
}

/**
 * Where an angle mark's measure is written, in world units, including any drag the user applied.
 *
 * The painters place the number in *screen* space, because a mark is notation and keeps its size
 * at every zoom. Hit-testing and dragging need the same place in world units, and the two agree
 * only through {@link angleMarkValueDistance} — the rule, not the number.
 *
 * They agree exactly at unit zoom and drift with it, which is the same approximation
 * {@link anchorPosition} has always made for the caption. It is the right trade here: the
 * alternative is to give the domain a camera, and a pick point a few units out is a worse
 * outcome than a layer boundary crossed.
 */
export function angleMarkValuePosition(
  value: Extract<Evaluated, { kind: 'angle-mark' }>
): Vec2 | null {
  const bisector = vec.add(value.from, value.to);
  const spread = vec.length(bisector);
  // Arms exactly opposite: no inside to the corner, so no wedge to write a number in.
  if (spread < 1e-9) return null;

  const radius = angleMarkRadius(value.armLength, value.size, value.showValue);
  const distance = angleMarkValueDistance(radius, value.degrees, value.ticks);
  return vec.add(vec.add(value.vertex, vec.scale(bisector, distance / spread)), value.valueOffset);
}

/**
 * Re-parameterises a locus point after its host changes shape.
 *
 * Used by the drag tool: it projects the pointer onto the host and returns the parameter
 * to store. Exposed here rather than in the tool so the exactness rule — a locus point is
 * *always* `pointAt(host, t)` — lives with the evaluator that enforces it.
 */
export function parameterForLocus(host: Curve, pointer: Vec2): number {
  return parameterAt(host, pointer);
}

/** The curve a locus point can be attached to, or `null` if the value is not a curve. */
export function locusHost(value: Evaluated | null, edge?: number): Curve | null {
  return hostCurve(value, edge);
}

/** Exposed for the snapping system in M7. */
export { asCurve, asLine, asPoint, closestPoint, hostCurve, intersectCurves, pointAt };

/**
 * Scales sampled points into world units and cuts them at the top and bottom of the window.
 *
 * Cutting **breaks the run** rather than clamping. Clamping would draw a flat line along the
 * top of the window wherever the function left it, which reads as part of the graph and is a
 * lie about the function — the curve genuinely is not there.
 */
function clipToWindow(
  segments: readonly (readonly Vec2[])[],
  bottom: number,
  top: number,
  scale: number,
  scaleY: number
): readonly (readonly Vec2[])[] {
  const out: Vec2[][] = [];
  let run: Vec2[] = [];

  const close = (): void => {
    if (run.length >= 2) out.push(run);
    run = [];
  };

  for (const segment of segments) {
    for (const point of segment) {
      // Two bounds rather than one magnitude: the frame is no longer centred on the axis, and
      // `|y| > limit` would cut `y = x²` at −9 as eagerly as at +9 in a frame running 0 to 9.
      if (point.y < bottom || point.y > top) {
        close();
        continue;
      }
      run.push(vec.vec2(point.x * scale, point.y * scaleY));
    }
    close();
  }

  return out;
}

/**
 * The geometry of a pair of axes.
 *
 * ## Why the ticks are built here
 *
 * Canvas, SVG and PDF all draw these. Arithmetic left to the back-ends is arithmetic that can
 * be got three subtly different ways, and the failure is a printed worksheet whose ticks sit a
 * pixel off the ones on screen. The evaluator computes them once and the three draw what they
 * are given — the same rule a solid's edges already follow.
 *
 * ## Why the tick step is clamped
 *
 * A step of zero would loop forever, and a step far smaller than the extent produces a solid
 * black bar rather than a scale. The clamp keeps a mis-typed number from making the editor
 * unusable, which matters because the step is exposed to the user.
 */
interface AxesRequest {
  readonly scale: number;
  /** The y axis's own unit. Equal units when it matches `scale`. */
  readonly scaleY: number;
  readonly extent: number;
  readonly extentY: number;
  readonly step: number;
  /** The y axis's own step. */
  readonly stepY: number;
  readonly numbered: boolean;
  readonly center: Vec2;
}

function buildAxes(request: AxesRequest): Evaluated | null {
  const { scale, scaleY, extent, extentY, numbered, center } = request;

  if (!Number.isFinite(scale) || !Number.isFinite(extent) || scale <= 0 || extent <= 0) return null;
  if (!Number.isFinite(scaleY) || scaleY <= 0) return null;
  if (!Number.isFinite(extentY) || extentY <= 0) return null;
  if (!Number.isFinite(center.x) || !Number.isFinite(center.y)) return null;

  const left = (center.x - extent) * scale;
  const right = (center.x + extent) * scale;
  const bottom = (center.y - extentY) * scaleY;
  const top = (center.y + extentY) * scaleY;

  /*
   * Each axis guarded against its own extent, not against the longer of the two.
   *
   * The guard used to take the larger, so that a step small enough for a short axis could not
   * draw hundreds of ticks up a long one. With one step for both that was the only safe reading;
   * with a step per axis it is simply wrong — it silently coarsened the short axis to suit the
   * long one, which is the thing having two steps is for.
   *
   * ## And a second guard, about room rather than about count
   *
   * A count cannot answer "will these numbers fit". Twelve ticks are comfortable across a wide
   * frame and touching across a narrow one, because the number written at a tick is a fixed size
   * on the page while the gap between ticks is not. A curve fitted to a four-unit window at forty
   * world units to the unit gets a step of 0.5 — nine labels twenty units apart, each of them
   * wider than that — and the axis comes out as the grey smear `window.ts` warns about.
   *
   * So the step also has to clear the width of a label, which needs the *scale*: only here is
   * both known. The result is rounded up to a round number, because the point of a scale is that
   * a reader can find 1 and 2 on it, and a step of 0.85 is not a scale anybody reads.
   *
   * A number is wider than it is tall, so the two axes need different room — and an unnumbered
   * pair needs none at all, because it has no labels to collide.
   */
  const roomFor = (given: number, own: number, unit: number, room: number): number => {
    const floor = Math.max(given, own / MAX_TICKS_PER_AXIS);
    return numbered ? niceAtLeast(Math.max(floor, room / unit)) : floor;
  };

  const safeStep = roomFor(request.step, extent, scale, NUMBER_ROOM_ACROSS);
  const safeStepY = roomFor(request.stepY, extentY, scaleY, NUMBER_ROOM_DOWN);
  if (!Number.isFinite(safeStep) || safeStep <= 0) return null;
  if (!Number.isFinite(safeStepY) || safeStepY <= 0) return null;

  const ticks: Segment[] = [];
  const marks: { at: Vec2; text: string }[] = [];

  /*
   * Ticks at multiples of the step, found inside the frame rather than counted out from its
   * middle.
   *
   * The loop used to start at the origin and step outward, which is the same thing only while
   * the frame is centred there. Off-centre it puts a tick at `centre + step`, so a frame centred
   * at 2.5 with a step of 1 would be marked at 3.5, 4.5, 5.5 — a scale whose numbers are all
   * halves, on a figure whose whole purpose is to be read off. Rounding into the frame instead
   * makes the marks fall on the round numbers a reader is looking for, wherever the frame sits.
   *
   * Numbers sit *below* the x axis and *left* of the y axis, on the side a reader expects, and
   * the origin is numbered once — two zeroes on top of each other is the classic beginner's axis.
   */
  const marksOn = (
    from: number,
    to: number,
    every: number,
    place: (unit: number) => { tick: Segment; mark: Vec2 }
  ): void => {
    const first = Math.ceil(from / every - EPSILON);
    const last = Math.floor(to / every + EPSILON);
    // A frame narrower than one step has no tick to show, which is honest — but a runaway loop
    // here would hang the editor, so the bound is a count and not a comparison on a growing sum.
    for (let index = first; index <= last; index += 1) {
      const unit = index * every;
      if (Math.abs(unit) < EPSILON) continue;
      const { tick, mark } = place(unit);
      ticks.push(tick);
      if (numbered) marks.push({ at: mark, text: formatUnit(unit) });
    }
  };

  marksOn(center.x - extent, center.x + extent, safeStep, (unit) => {
    const x = unit * scale;
    return {
      tick: { a: vec.vec2(x, -TICK_HALF_LENGTH), b: vec.vec2(x, TICK_HALF_LENGTH) },
      mark: vec.vec2(x, -NUMBER_GAP),
    };
  });

  marksOn(center.y - extentY, center.y + extentY, safeStepY, (unit) => {
    const y = unit * scaleY;
    return {
      tick: { a: vec.vec2(-TICK_HALF_LENGTH, y), b: vec.vec2(TICK_HALF_LENGTH, y) },
      mark: vec.vec2(-NUMBER_GAP, y),
    };
  });

  /*
   * The letters, and the origin written as `O` rather than as `0`.
   *
   * Both are what a Vietnamese textbook draws, and neither is decoration. `x` and `y` at the
   * arrowheads say which axis is which — without them a reader coming to the figure cold has to
   * infer it from the numbers, and on a frame where one axis carries a compressed unit that
   * inference is exactly the one they must not make by eye.
   *
   * `O` rather than `0` because the origin is a *point* of the figure, named the way every other
   * point in the editor is named, and problems refer to it by that letter — "khoảng cách từ O
   * đến". A `0` there reads as the first number on two scales at once, which is also why it was
   * only ever written once.
   *
   * All three follow the axes rather than being assumed: an off-centre frame can exclude the
   * origin, and what it must not get is an `O` floating at the corner of a frame with no origin
   * in it.
   */
  const showsOrigin = left <= 0 && right >= 0 && bottom <= 0 && top >= 0;
  const names: { at: Vec2; text: string }[] = [];

  if (numbered) {
    if (showsOrigin) names.push({ at: vec.vec2(-NUMBER_GAP, -NUMBER_GAP), text: 'O' });
    // Beyond each arrowhead, on the side the numbers are not, so a letter never lands on one.
    names.push({ at: vec.vec2(right + NAME_GAP, -NUMBER_GAP), text: 'x' });
    names.push({ at: vec.vec2(-NUMBER_GAP, top + NAME_GAP), text: 'y' });
  }

  return {
    kind: 'axes',
    origin: vec.vec2(0, 0),
    // Along `y = 0` and `x = 0`, clipped to the frame. These are the axes, not the edges of the
    // paper: moving the window does not move the origin.
    xAxis: { a: vec.vec2(left, 0), b: vec.vec2(right, 0) },
    yAxis: { a: vec.vec2(0, bottom), b: vec.vec2(0, top) },
    ticks,
    marks,
    names,
  };
}

/**
 * How far past the arrowhead an axis's letter sits, in world units.
 *
 * Clear of the arrow rather than beside it: a letter touching the head reads as part of it, and
 * the head is what says which way the numbers grow.
 */
const NAME_GAP = 14;

/**
 * Where the formula goes when nobody has moved it.
 *
 * ## Why not beside the start of the curve
 *
 * That is where it used to go, and it read well in the one figure it was written for. Elsewhere
 * it is the worst place available: a curve's first point is at the left edge of the plot, usually
 * near an axis, so the formula landed on the numbers along `Ox` — and because it is the longest
 * piece of text a figure carries, hanging it off the edge dragged the exported frame out with it.
 * Measured on `y = 2x³ + 5x² + 5x + 2`: an image 452 units wide for a drawing 208 wide, the rest
 * of it there to hold the caption.
 *
 * ## Where it goes instead
 *
 * Inside the plot, in whichever half the curve is not using. A cubic climbing left to right
 * leaves the top-left free; a parabola opening upward leaves the bottom. Counting the plotted
 * points either side of the middle is enough to tell which — it needs no notion of what the
 * function *is*, so it behaves the same on a formula nobody anticipated.
 *
 * It is anchored at the **left** and runs right, because that is the one direction the shared
 * text layout writes in. Anchoring at the right would need the width of the text, which needs a
 * font, which is exactly what the evaluator has none of.
 */
function formulaAnchor(
  segments: readonly (readonly Vec2[])[],
  plot: {
    readonly minX: number;
    readonly maxX: number;
    readonly minY: number;
    readonly maxY: number;
    readonly offset: Vec2;
  }
): Vec2 | null {
  const points = segments.flat();
  if (points.length === 0) return null;

  const middle = (plot.minY + plot.maxY) / 2;
  const above = points.filter((point) => point.y > middle).length;

  const inset = Math.max((plot.maxY - plot.minY) * LABEL_INSET, 1);
  const y = above * 2 > points.length ? plot.minY + inset : plot.maxY - inset;

  /*
   * And on the roomier side of `Oy`, which is drawn at `x = 0` whatever the frame.
   *
   * Anchoring at the left edge regardless meant the writing set off towards the y axis and, on
   * any frame with the axis near its middle, straight through it. Starting on the wide side
   * instead gives it the most clear run available before it reaches anything.
   *
   * It cannot always be enough: a formula wider than the whole plot crosses the axis from any
   * starting point, and `y = 2x³ + 5x² + 5x + 2` on a four-unit window is exactly that. Which is
   * the case `labelOffset` is for.
   */
  const insideFrame = plot.minX < 0 && plot.maxX > 0;
  const x = insideFrame && plot.maxX >= -plot.minX ? inset : plot.minX + inset;

  return vec.add(vec.vec2(x, y), plot.offset);
}

/** How far inside the plot the formula sits, as a fraction of the plot's height. */
const LABEL_INSET = 0.08;

/**
 * How much room a tick number needs, in world units.
 *
 * Enough for the widest a school axis actually writes — a sign, two digits and a decimal, at the
 * size `numberInk` sets — plus a gap, so neighbours are separated rather than merely not
 * overlapping. Across is the larger because a number is wider than it is tall; down, only the
 * line height has to clear.
 *
 * Approximate on purpose. The exact width needs a font and a measuring context, neither of which
 * belongs in the evaluator — and the three painters each have their own. A step is a round number
 * chosen from a handful of candidates, so a better measurement would almost never change which
 * one comes out.
 */
const NUMBER_ROOM_ACROSS = 34;
const NUMBER_ROOM_DOWN = 20;

/**
 * The smallest round number at least this large.
 *
 * One, two or five times a power of ten — the numbers a reader expects to find on a scale. A step
 * raised to make room has to land on one of these or it stops being a scale.
 */
function niceAtLeast(minimum: number): number {
  if (!Number.isFinite(minimum) || minimum <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(minimum));
  for (const multiple of [1, 2, 5]) {
    if (multiple * magnitude >= minimum) return multiple * magnitude;
  }
  return 10 * magnitude;
}

/** Half the length of a tick, in world units. Small enough to read as a mark, not a grid line. */
const TICK_HALF_LENGTH = 4;

/**
 * How far a number sits from its axis, in world units.
 *
 * Shared by the axes' own ticks and by the value a guide writes where it lands, which is the
 * point: a guide's number that sat at a different distance from `Ox` than the ticks either side
 * of it would read as a different kind of thing.
 */
const NUMBER_GAP = 12;

/** How far a coordinate sits from the dot it belongs to, in world units. */
const MARK_LABEL_GAP = 13;

/**
 * Which way a coordinate is written, decided by what kind of point it is.
 *
 * An entity's *name* is steered around the ink by `namePlacement`, which measures the drawing.
 * This text is part of an evaluated value, so the renderer draws it where the evaluator put it —
 * and a fixed corner is not good enough: up-and-right at a minimum writes the coordinate along
 * the rising arm of the curve, which is the one place it must not go.
 *
 * Knowing the kind is better than measuring, here, because the kind *is* the shape. Both arms
 * leave a maximum downward, so the whole space above it is empty; both leave a minimum upward.
 * A root sits on `Ox` with the curve crossing it, so the text goes below the axis where the
 * curve is on one side only; an intercept sits on `Oy` and goes to its left. That is what the
 * measured rule worked out for these four cases anyway, arrived at without the measuring.
 */
function markLabelOffset(kind: NotableKind): Vec2 {
  switch (kind) {
    case NotableKind.Maximum:
      return vec.vec2(0, MARK_LABEL_GAP);
    case NotableKind.Minimum:
      return vec.vec2(0, -MARK_LABEL_GAP);

    /*
     * Past the axis numbers, not merely past the dot.
     *
     * A root sits *on* `Ox`, where the ticks already have a row of numbers at `NUMBER_GAP`. A
     * coordinate one gap below the dot lands in that row and prints across it — measured on
     * `x²−4x+3`, whose roots at 1 and 3 wrote over the ticks at 0, 2 and 4. Clearing the row
     * costs nothing, and an intercept has the same problem down the left of `Oy`.
     */
    case NotableKind.Root:
      return vec.vec2(0, -(NUMBER_GAP + MARK_LABEL_GAP));
    case NotableKind.Intercept:
      return vec.vec2(-(NUMBER_GAP + MARK_LABEL_GAP), 0);
  }
}

/** Where a guide's value sits on each axis: below `Ox`, left of `Oy`, as the ticks' own do. */
const AXIS_NUMBER_BELOW: Vec2 = Object.freeze(vec.vec2(0, -NUMBER_GAP));
const AXIS_NUMBER_LEFT: Vec2 = Object.freeze(vec.vec2(-NUMBER_GAP, 0));

/** `(2; -1)` — the semicolon a Vietnamese figure prints, and a hyphen a PDF font can hold. */
function coordinateText(point: { readonly x: number; readonly y: number }): string {
  return `(${formatQuantity(point.x)}; ${formatQuantity(point.y)})`;
}

/** A step finer than this many divisions is a black bar, not a scale. */
const MAX_TICKS_PER_AXIS = 200;

/** A tick number, without the floating-point dust that `0.1 + 0.1 + 0.1` leaves behind. */
function formatUnit(value: number): string {
  return String(Number(value.toPrecision(12)));
}

/**
 * The variation table of whatever the parent graph turned out to be.
 *
 * It takes the *evaluated* graph rather than the source, so it is handed the expression the
 * curve was actually drawn from — one formula, read once, and no way for the table to describe
 * a function the picture does not show.
 *
 * The range is the table's own decision and it is made here rather than stored. Preferring the
 * whole line is what produces the table a textbook prints for `x^3-3x`; falling back to the
 * graph's own window is what gives `sin(x)` a table over the arch the user is looking at
 * instead of refusing outright. Storing the choice would mean a table that silently kept a
 * range chosen for a formula that has since been edited.
 */
function buildVariationGeometry(graph: Evaluated | null, at: Vec2): Evaluated | null {
  if (graph?.kind !== 'function-graph') return null;

  const result = variationTableFor(graph.expression, { from: graph.from, to: graph.to });
  if (result?.ok !== true) return null;

  const geometry = layOutVariationTable(result.table, at);
  return {
    kind: 'variation-table',
    rules: geometry.rules,
    marks: geometry.marks,
    corner: geometry.corner,
    width: geometry.width,
    height: geometry.height,
  };
}

/**
 * Where two segments that cross on the page cross *in space*, or `null` when they do not.
 *
 * ## Why this can be exact rather than a guess
 *
 * A projection is affine, so it carries a segment to a segment and preserves the parameter
 * along it: the point three-fifths of the way down `AC` in space lands three-fifths of the way
 * down `AC` on the page. Read the parameter of the page crossing off one segment, walk the same
 * fraction along its counterpart in space, and that is the pre-image of the crossing — no
 * approximation anywhere.
 *
 * ## Why it still has to check
 *
 * Because two segments crossing on the page need not meet at all: on a cube, an edge in front
 * and an edge behind cross constantly in the drawing and are metres apart in the solid. That is
 * exactly why `intersect` has always been a page operation and has always refused to claim
 * otherwise.
 *
 * The test is the pre-image taken from *both* segments. If the two agree, the lines genuinely
 * meet there. If they do not, the lines are skew and the crossing exists only in the drawing —
 * and the answer is `null`, which leaves the point exactly as page-bound as it was before.
 *
 * ## What it buys
 *
 * `O = intersect AC BD` — the way a problem says "tâm đáy là giao điểm hai đường chéo" — now
 * yields a point that knows where it is, so `SO = segment S O` is drawn **dashed**, because the
 * pyramid is in front of it. Before this, that figure showed the height of a pyramid as a solid
 * line running through the middle of the solid: a drawing stating something false, silently,
 * in one of the commonest constructions there is.
 */
function crossingInSpace(
  first: Evaluated | null | undefined,
  second: Evaluated | null | undefined,
  at: Vec2
): Vec3 | null {
  const along = (value: Evaluated | null | undefined): Vec3 | null => {
    if (value?.kind !== 'segment' || value.in3d === undefined) return null;

    const { a, b } = value.segment;
    const span = vec.subtract(b, a);
    const lengthSquared = vec.dot(span, span);
    if (lengthSquared < EPSILON) return null;

    const t = vec.dot(vec.subtract(at, a), span) / lengthSquared;
    const [from, to] = value.in3d;
    return vec3(
      from.x + (to.x - from.x) * t,
      from.y + (to.y - from.y) * t,
      from.z + (to.z - from.z) * t
    );
  };

  const onFirst = along(first);
  const onSecond = along(second);
  if (onFirst === null || onSecond === null) return null;

  /*
   * Generous, and deliberately so. The two routes differ by the arithmetic of a projection and
   * an inverse, on coordinates that are hundreds of units across; a tolerance tight enough to
   * be pure would reject crossings that are genuinely the same point. Skew edges on a solid
   * miss each other by a large fraction of the solid, so nothing sits near this line.
   */
  const apart = Math.hypot(onFirst.x - onSecond.x, onFirst.y - onSecond.y, onFirst.z - onSecond.z);
  return apart <= SKEW_TOLERANCE ? onFirst : null;
}

/** How far apart the two pre-images may be and still count as one point, in world units. */
const SKEW_TOLERANCE = 0.5;
